import { useLocalStorage } from "@mantine/hooks";
import {
  THREESHAPE_OAUTH_CODE_VERIFIER_KEY,
  THREESHAPE_OAUTH_STATE_KEY,
} from "../constants/threeshape";
import { getEnvironment } from "../utils/environment";
import { useMutation } from "@apollo/client";
import { RegenerateThreeshapeTokensDocument } from "../generated/graphql";

export const useThreeShapeOAuth = () => {
  const [state, setState, deleteState] = useLocalStorage({
    key: THREESHAPE_OAUTH_STATE_KEY,
  });
  const [codeVerifier, setCodeVerifier, deleteCodeVerifier] = useLocalStorage({
    key: THREESHAPE_OAUTH_CODE_VERIFIER_KEY,
  });
  const [regenerateThreeshapeTokens, { loading: isRegeneratingTokens }] =
    useMutation(RegenerateThreeshapeTokensDocument);

  const generateAndSaveState = () => {
    const state = btoa(
      String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16)))
    )
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");

    setState(state);

    return state;
  };

  const generateAndSaveCodeVerifier = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);

    const codeVerifier = btoa(String.fromCharCode(...array))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");

    setCodeVerifier(codeVerifier);

    return codeVerifier;
  };

  const getCodeChallenge = async (codeVerifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(digest));
    const hashBase64 = btoa(String.fromCharCode(...hashArray))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
    return hashBase64;
  };

  const get3ShapeOAuthBaseUrl = () => {
    const environment = getEnvironment();

    switch (environment) {
      case "staging":
        return "https://staging-identity.3shape.com/connect/authorize";
      default:
        return "https://identity.3shape.com/connect/authorize";
    }
  };

  const getClientId = () => {
    const environment = getEnvironment();

    switch (environment) {
      case "staging":
        return "JasperDental.Staging";
      default:
        return "JspEV61QDFTDGPXal";
    }
  };

  const get3ShapeOAuthUrl = async () => {
    const codeVerifier = generateAndSaveCodeVerifier();
    const codeChallenge = await getCodeChallenge(codeVerifier);
    const state = generateAndSaveState();

    const redirectUri = `${window.location.protocol}//${window.location.host}/orders/callback/3shape`;
    const scope =
      "openid api offline_access communicate.connections.manage data.companies.read_only data.users.read_only";

    const oauthUrl = new URL(get3ShapeOAuthBaseUrl());

    oauthUrl.searchParams.append("client_id", getClientId());
    oauthUrl.searchParams.append("response_type", "code");
    oauthUrl.searchParams.append("scope", scope);
    oauthUrl.searchParams.append("redirect_uri", redirectUri);
    oauthUrl.searchParams.append("code_challenge", codeChallenge);
    oauthUrl.searchParams.append("code_challenge_method", "S256");
    oauthUrl.searchParams.append("response_mode", "query");
    oauthUrl.searchParams.append("state", state);

    return oauthUrl.toString();
  };

  return {
    resetFlow: () => {
      deleteState();
      deleteCodeVerifier();
    },
    get3ShapeOAuthUrl,
    state,
    codeVerifier,
    isRegeneratingTokens,
    regenerateThreeshapeTokens: ({
      code,
      codeVerifier,
    }: {
      code: string;
      codeVerifier: string;
    }) => {
      return regenerateThreeshapeTokens({
        variables: {
          code,
          codeVerifier,
          redirectUri: `${window.location.protocol}//${window.location.host}/orders/callback/3shape`,
        },
      });
    },
  };
};
